import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Breadcrumbs, CircularProgress, Divider, Link, Snackbar, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Button from '@mui/material/Button';
import TopicContentEditor from '../components/tutor/content/TopicContentEditor';
import ExtraResourcesPanel from '../components/tutor/content/ExtraResourcesPanel';
import TutorNodeQuizList from '../components/tutor/quiz/TutorNodeQuizList';
import TutorNodeQuizConfigPanel from '../components/tutor/quiz/TutorNodeQuizConfigPanel';
import { TutorTopicContentDTO, getContent, saveContent } from '../services/tutorTopicContentService';
import { TutorResolvedConfigDTO, getNodeQuizConfig } from '../services/tutorQuizService';

export default function TutorTopicContent() {
  const { courseId, nodeId } = useParams<{ courseId: string; nodeId: string }>();
  const navigate = useNavigate();
  const cId = Number(courseId);
  const nId = Number(nodeId);

  const [dto, setDto] = useState<TutorTopicContentDTO | null>(null);
  const [editorContent, setEditorContent] = useState('');
  const [resolvedConfig, setResolvedConfig] = useState<TutorResolvedConfigDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    if (!cId || !nId) return;
    setLoading(true);
    Promise.all([getContent(cId, nId), getNodeQuizConfig(cId, nId)])
      .then(([contentData, configData]) => {
        setDto(contentData);
        setEditorContent(contentData.content);
        setResolvedConfig(configData);
      })
      .catch(() => {
        setSnackbar({ open: true, message: 'Failed to load content.', severity: 'error' });
      })
      .finally(() => setLoading(false));
  }, [cId, nId]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updated = await saveContent(cId, nId, editorContent);
      setDto(updated);
      setSnackbar({ open: true, message: 'Content saved.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to save content.', severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link underline="hover" sx={{ cursor: 'pointer' }} onClick={() => navigate('/tutor/courses')}>
          My Courses
        </Link>
        <Link
          underline="hover"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate(`/tutor/courses/${cId}/structure`)}
        >
          Structure
        </Link>
        <Typography color="text.primary">Topic Content</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          size="small"
          onClick={() => navigate(`/tutor/courses/${cId}/structure`)}
        >
          Back to Structure
        </Button>
        <Typography variant="h5" fontWeight={600} sx={{ ml: 1 }}>
          Topic Content
        </Typography>
      </Box>

      {dto && (
        <TopicContentEditor
          courseId={cId}
          nodeId={nId}
          content={editorContent}
          isInherited={dto.isInherited}
          hasCustomContent={dto.hasCustomContent}
          onChange={setEditorContent}
          onSave={handleSave}
          isSaving={isSaving}
        />
      )}

      <ExtraResourcesPanel courseId={cId} nodeId={nId} />

      <Divider sx={{ my: 3 }} />
      <Box>
        <TutorNodeQuizConfigPanel courseId={cId} nodeId={nId} />
        <Box mt={3}>
          {resolvedConfig && (
            <TutorNodeQuizList courseId={cId} nodeId={nId} resolvedConfig={resolvedConfig} />
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
